import React from 'react';
import { T, NUMERIC, money } from '../customer/customers.theme';
import { PlusIcon, SearchIcon, Spinner } from '../customer/CustomersUI';
import { SearchField, Thumbnail } from '../menu/MenuUI';
import { initialsOf } from '../menu/menuApi';
import { COPY } from './stock.copy';

/**
 * What is on the shelf, down the left.
 *
 * The same column as the customers and vendors books — fixed width, its own
 * search, and it stays put while the right-hand side changes — because working
 * a store means hopping between items.
 *
 * The three tabs are the phone's two plus one: the phone offers All and Out of
 * stock, and `reorderLevel` is a field the phone never sets and the model says
 * out loud is "used by the desktop screen". Low is that field finally doing
 * something.
 */

/** Out, low, or fine. The only classification this screen makes. */
export function statusOf(item) {
  const quantity = Number(item?.quantity || 0);
  if (quantity <= 0) return 'out';
  const reorder = Number(item?.reorderLevel || 0);
  if (reorder > 0 && quantity <= reorder) return 'low';
  return 'ok';
}

function Tab({ label, count, active, tone, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 7,
        height: 46,
        border: 'none',
        borderBottom: `2px solid ${active ? T.brand : 'transparent'}`,
        background: 'transparent',
        font: 'inherit',
        fontSize: 13,
        fontWeight: 600,
        color: active ? T.ink : T.inkSoft,
        cursor: 'pointer',
      }}
    >
      {label}
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          padding: '1px 7px',
          borderRadius: T.radiusPill,
          background: active ? tone.bg : T.shade,
          color: active ? tone.ink : T.inkSoft,
          ...NUMERIC,
        }}
      >
        {count}
      </span>
    </button>
  );
}

export default function StockList({
  stocks,
  stockValue,
  currency,
  query,
  onQueryChange,
  tab,
  onTabChange,
  onOpen,
  onAdd,
  canWrite,
  loading,
}) {
  const counts = React.useMemo(
    () => ({
      all: stocks.length,
      low: stocks.filter((item) => statusOf(item) === 'low').length,
      out: stocks.filter((item) => statusOf(item) === 'out').length,
    }),
    [stocks]
  );

  const visible = React.useMemo(() => {
    const needle = query.trim().toLowerCase();

    const rows = stocks.filter((item) => {
      const matchesTab = tab === 'all' || statusOf(item) === tab;
      const matchesQuery = !needle || String(item.name || '').toLowerCase().indexOf(needle) !== -1;
      return matchesTab && matchesQuery;
    });

    // Alphabetical on All, because that is how somebody looks for a name; by
    // what is emptiest on the other two, because that is the question those
    // tabs are asking.
    return rows.sort((a, b) =>
      tab === 'all'
        ? String(a.name || '').localeCompare(String(b.name || ''))
        : Number(a.quantity || 0) - Number(b.quantity || 0)
    );
  }, [stocks, tab, query]);

  return (
    <div
      style={{
        width: T.listWidth,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        background: T.surface,
        borderRight: `1px solid ${T.border}`,
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', borderBottom: `1px solid ${T.border}` }}>
        <Tab
          label={COPY.allStock}
          count={counts.all}
          active={tab === 'all'}
          tone={{ bg: T.ink, ink: T.surface }}
          onClick={() => onTabChange('all')}
        />
        <Tab
          label={COPY.low}
          count={counts.low}
          active={tab === 'low'}
          tone={{ bg: T.pendingTint, ink: T.pendingInk }}
          onClick={() => onTabChange('low')}
        />
        <Tab
          label={COPY.outBadge}
          count={counts.out}
          active={tab === 'out'}
          tone={{ bg: T.dangerTint, ink: T.danger }}
          onClick={() => onTabChange('out')}
        />
      </div>

      <div
        style={{
          display: 'flex',
          gap: 8,
          padding: '14px 16px 12px',
          borderBottom: `1px solid ${T.hairline}`,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <SearchField
            value={query}
            onChange={onQueryChange}
            placeholder={COPY.searchStock}
            icon={<SearchIcon />}
          />
        </div>

        {canWrite ? (
          <button
            type="button"
            onClick={onAdd}
            title={COPY.newItem}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              height: 42,
              padding: '0 14px',
              border: `1px solid ${T.borderStrong}`,
              borderRadius: T.radiusPill,
              background: T.surface,
              font: 'inherit',
              fontSize: 13,
              fontWeight: 600,
              color: T.ink,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            <PlusIcon />
            New
          </button>
        ) : null}
      </div>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading && !stocks.length ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40, color: T.inkFaint }}>
            <Spinner size={20} />
          </div>
        ) : null}

        {!loading && !visible.length ? (
          <div style={{ padding: '40px 24px', textAlign: 'center', fontSize: 13.5, color: T.inkSoft }}>
            {query.trim()
              ? COPY.nothingCalled(query.trim())
              : tab === 'low'
              ? 'Nothing is running low.'
              : tab === 'out'
              ? 'Nothing is out of stock.'
              : COPY.nothingYet}
          </div>
        ) : null}

        {visible.map((item) => {
          const state = statusOf(item);
          const quantity = Number(item.quantity || 0);

          return (
            <button
              key={item._id}
              type="button"
              onClick={() => onOpen(item)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                width: '100%',
                padding: '11px 16px',
                border: 'none',
                borderBottom: `1px solid ${T.hairline}`,
                background: 'transparent',
                font: 'inherit',
                textAlign: 'left',
                cursor: 'pointer',
              }}
            >
              <div style={{ width: 42, flexShrink: 0 }}>
                <Thumbnail
                  src={item.imageUrl}
                  initials={initialsOf(item.name)}
                  height={42}
                  radius={8}
                />
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: T.ink,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {item.name}
                </div>
                <div style={{ fontSize: 12, color: T.inkFaint, marginTop: 2, ...NUMERIC }}>
                  {money(item.cost, currency)} / {item.unit}
                </div>
              </div>

              <div style={{ flexShrink: 0, textAlign: 'right' }}>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: state === 'out' ? T.danger : T.ink,
                    ...NUMERIC,
                  }}
                >
                  {quantity}
                  <span style={{ fontSize: 11, fontWeight: 600, color: T.inkFaint, marginLeft: 3 }}>
                    {item.unit}
                  </span>
                </div>

                <div style={{ marginTop: 3 }}>
                  {state === 'out' ? (
                    <span
                      style={{
                        fontSize: 10.5,
                        fontWeight: 700,
                        padding: '2px 8px',
                        borderRadius: T.radiusPill,
                        background: T.dangerTint,
                        color: T.danger,
                        border: `1px solid ${T.dangerBorder}`,
                      }}
                    >
                      {COPY.outBadge}
                    </span>
                  ) : state === 'low' ? (
                    <span
                      style={{
                        fontSize: 10.5,
                        fontWeight: 700,
                        padding: '2px 8px',
                        borderRadius: T.radiusPill,
                        background: T.pendingTint,
                        color: T.pendingInk,
                        border: `1px solid ${T.pendingBorder}`,
                      }}
                    >
                      {COPY.lowBadge}
                    </span>
                  ) : (
                    <span style={{ fontSize: 12, color: T.inkFaint, ...NUMERIC }}>
                      {money(Math.max(0, quantity) * Number(item.cost || 0), currency)}
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          borderTop: `1px solid ${T.border}`,
          background: T.sunk,
          fontSize: 12.5,
          color: T.inkSoft,
        }}
      >
        <span>{COPY.itemsCount(stocks.length)}</span>
        <span>
          {COPY.stockValue}{' '}
          <b style={{ fontSize: 14, color: T.ink, ...NUMERIC }}>{money(stockValue, currency)}</b>
        </span>
      </div>
    </div>
  );
}
